import { useState, forwardRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useStateValue } from "../context/state/provider";
import Navbar from "./Navbar";
import ChatFooter from "../components/ChatFooter";
import ChatPanel from "./ChatPanel";
import AccessibilityPanel from "./AccessibilityPanel";
import LoginPanel from "./LoginPanel";
import SettingsPanel from "./SettingsPanel";
import { VIEW } from "../constants/view.constants";
import Alert from "@material-ui/lab/Alert";

const useStyles = makeStyles({
	page: {
		width: "100%",
		height: "100%",
		display: "flex",
		alignItems: "stretch",
		justifyContent: "center",
		boxSizing: "border-box",
		background: "transparent",
		padding: 0,
		animation: "fadeIn 0.3s ease",
	},
	chatBotBody: {
		position: "relative",
		display: "flex",
		flexDirection: "column",
		width: "100%",
		height: (props) => (props.isFullView ? "100%" : "calc(100% - 88px)"),
		maxHeight: (props) => (props.isFullView ? "100%" : "none"),
		margin: 0,
		backgroundColor: "var(--surface-bg)",
		overflow: "hidden",
		textAlign: "left",
		borderRadius: (props) =>
			props.isFullView ? 0 : "var(--radius-shell) var(--radius-shell) 0 0",
		border: (props) =>
			props.isFullView ? "none" : "1px solid var(--surface-border)",
		boxShadow: (props) =>
			props.isFullView ? "none" : "0 14px 40px rgba(15, 23, 42, 0.16)",
		animation: "fadeIn 0.3s ease",
	},
	main: {
		flex: "1 1 auto",
		minHeight: 0,
		display: "flex",
		flexDirection: "column",
		position: "relative",
		background: "linear-gradient(180deg, #fafbfc 0%, #ffffff 28%)",
	},
	alert: {
		position: "absolute",
		bottom: (props) => (props.isFullView ? "84px" : "96px"),
		right: 16,
		left: 16,
		fontSize: "13px",
		margin: "auto",
		zIndex: 5,
		borderRadius: 12,
		fontFamily: "var(--font-sans)",
	},
});

const ChatbotBody = forwardRef(({
	messages,
	messagesRef,
	brandingTitle,
	subtitle,
	footer,
	avatarUrl,
	firestore,
	error,
	online,
	fsLoading,
	userLoading,
	justOpen,
	handleJustOpen,
	chatbotIsDraft,
	user,
	userLoadingAU,
	chatbotId,
	handleFallbackMessage,
	clearFallbackMessage,
	isFullView = true,
}, ref) => {
	const classes = useStyles({ isFullView });
	const [{ view, loggingOut }] = useStateValue();
	const [errMsg, setErrMsg] = useState({ message: "", location: "" });
	const [showErrorMessage, setShowErrorMessage] = useState(false);

	return (
		<div className={classes.page}>
			<div className={classes.chatBotBody}>
				<Navbar
					firestore={firestore}
					view={view}
					brandingTitle={brandingTitle}
					subtitle={subtitle}
					avatarUrl={avatarUrl}
					user={user}
					chatbotId={chatbotId}
					isFullView={isFullView}
				/>

				<div className={classes.main}>
					{view === VIEW.CHATROOM && (
						<ChatPanel
							ref={ref}
							firestore={firestore}
							messagesRef={messagesRef}
							messages={messages}
							fsLoading={fsLoading}
							fsError={error}
							online={online}
							userLoading={userLoading}
							setErrMsg={setErrMsg}
							setShowErrorMessage={setShowErrorMessage}
							disableBtn={showErrorMessage}
							justOpen={justOpen}
							handleJustOpen={handleJustOpen}
							chatbotIsDraft={chatbotIsDraft}
							user={user}
							userLoadingAU={userLoadingAU}
							chatbotId={chatbotId}
							avatarUrl={avatarUrl}
							handleFallbackMessage={handleFallbackMessage}
							clearFallbackMessage={clearFallbackMessage}
							brandingTitle={brandingTitle}
							isFullView={isFullView}
						/>
					)}
					{view === VIEW.ACCESSIBILITY && <AccessibilityPanel />}
					{view === VIEW.LOGIN && <LoginPanel firestore={firestore} chatbotId={chatbotId} />}
					{view === VIEW.SETTINGS && <SettingsPanel />}

					{showErrorMessage && errMsg.location === "msgInput" && view === VIEW.CHATROOM && !loggingOut && (
						<Alert className={classes.alert} severity="error">
							{errMsg.message}
						</Alert>
					)}
				</div>
				<ChatFooter footer={footer} isFullView={isFullView} />
			</div>
		</div>
	);
});

export default ChatbotBody;
